import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  ArrowLeft, Bus, Save, RotateCcw, Plus, Trash2, 
  AlertTriangle, CheckCircle, Loader2, Grid3X3
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const AdminSeatMapEditor = () => {
  const { busId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busInfo, setBusInfo] = useState(null);
  const [layout, setLayout] = useState({
    rows: 12,
    seats_per_row: 4,
    aisle_after_seat: 2,
    sections: []
  });
  const [hasChanges, setHasChanges] = useState(false);
  // Track raw input for rows fields (allows typing commas)
  const [rowInputs, setRowInputs] = useState({});

  useEffect(() => {
    fetchBusLayout();
  }, [busId]);

  const fetchBusLayout = async () => {
    try {
      const response = await api.get(`/admin/bus/fleet/${busId}/seat-layout`);
      const data = response.data;
      
      setBusInfo({
        registrationNumber: data.registrationNumber,
        make: data.make,
        model: data.model,
        operatorName: data.operatorName,
        capacity: data.capacity
      });
      
      // Convert from camelCase to snake_case for internal state
      const seatLayout = data.seatLayout;
      setLayout({
        rows: seatLayout.rows || 12,
        seats_per_row: seatLayout.seatsPerRow || 4,
        aisle_after_seat: seatLayout.aisleAfterSeat || 2,
        sections: (seatLayout.sections || []).map(s => ({
          name: s.name,
          rows: s.rows || [],
          price_multiplier: s.priceMultiplier || 1.0
        }))
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to load seat layout',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLayoutChange = (field, value) => {
    setLayout(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSectionChange = (index, field, value) => {
    setLayout(prev => {
      const newSections = [...prev.sections];
      newSections[index] = { ...newSections[index], [field]: value };
      return { ...prev, sections: newSections };
    });
    setHasChanges(true);
  };

  const addSection = () => {
    const existingRows = layout.sections.flatMap(s => s.rows);
    const availableRows = [];
    for (let i = 1; i <= layout.rows; i++) {
      if (!existingRows.includes(i)) {
        availableRows.push(i);
      }
    }
    
    setLayout(prev => ({
      ...prev,
      sections: [
        ...prev.sections,
        { name: 'New Section', rows: availableRows.slice(0, 2), price_multiplier: 1.0 }
      ]
    }));
    setHasChanges(true);
  };

  const removeSection = (index) => {
    setLayout(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const handleRowsInputChange = (index, value) => {
    // Store raw input to allow typing commas
    setRowInputs(prev => ({ ...prev, [index]: value }));
    setHasChanges(true);
  };

  const handleRowsInputBlur = (index) => {
    // Parse comma-separated row numbers on blur
    const value = rowInputs[index];
    if (value === undefined) return;
    
    const rows = value
      .split(',')
      .map(r => parseInt(r.trim()))
      .filter(r => !isNaN(r) && r >= 1 && r <= layout.rows);
    
    handleSectionChange(index, 'rows', rows);
    // Clear the raw input so it uses the parsed value
    setRowInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[index];
      return newInputs;
    });
  };

  // Get display value for rows input (raw input or formatted array)
  const getRowsInputValue = (index, section) => {
    if (rowInputs[index] !== undefined) {
      return rowInputs[index];
    }
    return section.rows.join(', ');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/bus/fleet/${busId}/seat-layout`, layout);
      toast({
        title: 'Success',
        description: 'Seat layout saved successfully',
      });
      setHasChanges(false);
      fetchBusLayout(); // Refresh data
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save seat layout',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default layout? This will override all custom settings.')) {
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/admin/bus/fleet/${busId}/seat-layout/reset`);
      toast({
        title: 'Success',
        description: 'Seat layout reset to default',
      });
      fetchBusLayout();
      setHasChanges(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reset layout',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const getSectionForRow = (row) => {
    for (const section of layout.sections) {
      if (section.rows.includes(row)) {
        return section;
      }
    }
    return null;
  };

  const getSectionColor = (sectionName) => {
    const colors = {
      'VIP': 'bg-purple-500',
      'Business': 'bg-blue-500',
      'Standard': 'bg-gray-400',
      'Economy': 'bg-green-500',
      'Premium': 'bg-amber-500'
    };
    return colors[sectionName] || 'bg-gray-300';
  };

  const getSectionBgColor = (sectionName) => {
    const colors = {
      'VIP': 'bg-purple-100',
      'Business': 'bg-blue-100',
      'Standard': 'bg-gray-100',
      'Economy': 'bg-green-100',
      'Premium': 'bg-amber-100'
    };
    return colors[sectionName] || 'bg-gray-50';
  };

  // Generate seat columns based on seats_per_row
  const getSeatColumns = () => {
    const cols = ['A', 'B', 'C', 'D', 'E', 'F'].slice(0, layout.seats_per_row);
    return cols;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/bus')}
              className="p-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Grid3X3 className="h-6 w-6 text-pink-600" />
                Seat Map Editor
              </h1>
              {busInfo && (
                <p className="text-gray-600">
                  {busInfo.make} {busInfo.model} • {busInfo.registrationNumber} • {busInfo.operatorName}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <Badge className="bg-yellow-100 text-yellow-800">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Unsaved Changes
              </Badge>
            )}
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={saving}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-pink-600 hover:bg-pink-700"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Layout Configuration */}
          <div className="space-y-6">
            {/* Basic Settings */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Bus className="h-5 w-5 text-blue-600" />
                Bus Layout Settings
              </h2>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Rows
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={layout.rows}
                    onChange={(e) => handleLayoutChange('rows', parseInt(e.target.value) || 12)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Seats per Row
                  </label>
                  <Input
                    type="number"
                    min="2"
                    max="6"
                    value={layout.seats_per_row}
                    onChange={(e) => handleLayoutChange('seats_per_row', parseInt(e.target.value) || 4)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aisle After Seat
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max={layout.seats_per_row - 1}
                    value={layout.aisle_after_seat}
                    onChange={(e) => handleLayoutChange('aisle_after_seat', parseInt(e.target.value) || 2)}
                  />
                </div>
              </div>
              
              <p className="text-sm text-gray-500 mt-3">
                Total Capacity: <strong>{layout.rows * layout.seats_per_row} seats</strong>
              </p>
            </Card>

            {/* Sections */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Seat Sections & Pricing</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addSection}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Section
                </Button>
              </div>

              {layout.sections.length === 0 ? (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <Grid3X3 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No sections defined</p>
                  <p className="text-sm">Add sections to create different price tiers</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {layout.sections.map((section, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border ${getSectionBgColor(section.name)}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${getSectionColor(section.name)}`}></div>
                          <Input
                            value={section.name}
                            onChange={(e) => handleSectionChange(index, 'name', e.target.value)}
                            className="w-32 h-8 font-medium"
                            placeholder="Section name"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSection(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Rows (comma-separated)
                          </label>
                          <Input
                            value={getRowsInputValue(index, section)}
                            onChange={(e) => handleRowsInputChange(index, e.target.value)}
                            onBlur={() => handleRowsInputBlur(index)}
                            placeholder="e.g., 1, 2, 3"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Price Multiplier
                          </label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0.5"
                            max="5"
                            value={section.price_multiplier}
                            onChange={(e) => handleSectionChange(index, 'price_multiplier', parseFloat(e.target.value) || 1.0)}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-2">
                        {section.rows.length} rows • {section.rows.length * layout.seats_per_row} seats • {section.price_multiplier}x base price
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Unassigned Rows Warning */}
              {(() => {
                const assignedRows = layout.sections.flatMap(s => s.rows);
                const unassignedRows = [];
                for (let i = 1; i <= layout.rows; i++) {
                  if (!assignedRows.includes(i)) {
                    unassignedRows.push(i);
                  }
                }
                if (unassignedRows.length > 0) {
                  return (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Unassigned rows: {unassignedRows.join(', ')}
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">
                        These rows will use standard (1x) pricing
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            </Card>
          </div>

          {/* Live Preview */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Live Preview</h2>
            
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b">
              {layout.sections.map((section, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${getSectionColor(section.name)}`}></div>
                  <span className="text-sm">
                    {section.name} ({section.price_multiplier}x)
                  </span>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-300"></div>
                <span className="text-sm">Standard (1x)</span>
              </div>
            </div>

            {/* Bus Layout Preview */}
            <div className="bg-gray-100 rounded-xl p-4 overflow-x-auto">
              {/* Driver Area */}
              <div className="flex justify-center mb-4">
                <div className="bg-gray-300 rounded-lg px-6 py-2 text-sm text-gray-600">
                  DRIVER
                </div>
              </div>

              {/* Seat Grid */}
              <div className="space-y-2">
                {/* Column Headers */}
                <div className="flex justify-center gap-1 mb-2">
                  {getSeatColumns().map((col, idx) => (
                    <React.Fragment key={col}>
                      <div className="w-10 text-center text-xs font-medium text-gray-500">
                        {col}
                      </div>
                      {idx === layout.aisle_after_seat - 1 && (
                        <div className="w-6"></div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Rows */}
                {Array.from({ length: layout.rows }, (_, rowIndex) => {
                  const rowNum = rowIndex + 1;
                  const section = getSectionForRow(rowNum);
                  const seatColor = section ? getSectionColor(section.name) : 'bg-gray-300';
                  
                  return (
                    <div key={rowNum} className="flex items-center justify-center gap-1">
                      <div className="w-6 text-xs text-gray-500 text-right pr-1">
                        {rowNum}
                      </div>
                      {getSeatColumns().map((col, colIdx) => (
                        <React.Fragment key={`${rowNum}${col}`}>
                          <div
                            className={`w-10 h-8 rounded-t-lg ${seatColor} flex items-center justify-center text-white text-xs font-medium shadow-sm`}
                            title={`${rowNum}${col} - ${section?.name || 'Standard'}`}
                          >
                            {rowNum}{col}
                          </div>
                          {colIdx === layout.aisle_after_seat - 1 && (
                            <div className="w-6 text-center text-gray-400 text-xs">|</div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Back of Bus */}
              <div className="flex justify-center mt-4">
                <div className="bg-gray-300 rounded-lg px-12 py-2 text-sm text-gray-600">
                  REAR
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{layout.rows}</p>
                <p className="text-xs text-gray-600">Rows</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{layout.seats_per_row}</p>
                <p className="text-xs text-gray-600">Seats/Row</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{layout.rows * layout.seats_per_row}</p>
                <p className="text-xs text-gray-600">Total Seats</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSeatMapEditor;
